import { useEffect, useRef, useCallback, useState } from "react";
import { useFeature } from "@optimizely/react-sdk";
import { SHOW_ERROR_TOASTS } from "../../constants";
import {
  setupAxiosInterceptor,
  teardownAxiosInterceptor,
} from "./axiosErrorInterceptor";
import { errorToastCallback } from "./apolloErrorLink";
import ErrorConsolePanel from "./ErrorConsolePanel";

// Environments where error console should be shown
const DEV_ENVIRONMENTS = ["local", "development", "qa"];

// Time window to prevent duplicate errors (in milliseconds)
const DUPLICATE_WINDOW_MS = 3000;

let nextErrorId = 1;

/**
 * GlobalErrorHandler
 *
 * Catches unhandled JavaScript errors, promise rejections,
 * failed HTTP requests (axios), and GraphQL errors (Apollo),
 * collects them into a floating error console in dev/qa
 * environments when the feature flag is enabled, and always
 * logs to console.
 */
export const GlobalErrorHandler = ({ environment }) => {
  const [showErrorToasts] = useFeature(SHOW_ERROR_TOASTS);
  const [errors, setErrors] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const recentErrorsRef = useRef(new Set());

  const isDevOrQaEnvironment = DEV_ENVIRONMENTS.includes(environment);
  const shouldShowConsole = showErrorToasts && isDevOrQaEnvironment;

  const createErrorKey = useCallback((message) => {
    return message?.substring(0, 100) || "unknown_error";
  }, []);

  const isDuplicateError = useCallback((errorKey) => {
    if (recentErrorsRef.current.has(errorKey)) {
      return true;
    }

    recentErrorsRef.current.add(errorKey);

    setTimeout(() => {
      recentErrorsRef.current.delete(errorKey);
    }, DUPLICATE_WINDOW_MS);

    return false;
  }, []);

  /**
   * Adds an error/warning entry to the console panel.
   */
  const addError = useCallback(
    (message, type = "error") => {
      if (!shouldShowConsole) return;

      const errorKey = createErrorKey(message);
      if (isDuplicateError(errorKey)) return;

      setErrors((prev) => [
        {
          id: nextErrorId++,
          message,
          type,
          timestamp: new Date(),
        },
        ...prev,
      ]);
    },
    [shouldShowConsole, createErrorKey, isDuplicateError]
  );

  /**
   * Shared handler used by both the axios interceptor and
   * the Apollo error link — typed as "network".
   */
  const handleInterceptedError = useCallback(
    (message) => {
      addError(message, "network");
    },
    [addError]
  );

  // --- Unhandled JS errors & promise rejections ---

  const handleError = useCallback(
    (event) => {
      const errorMessage =
        event.error?.message || event.message || "An unknown error occurred";

      console.error(
        "[GlobalErrorHandler] Unhandled error:",
        event.error || event
      );

      addError(`Error: ${errorMessage}`, "error");
    },
    [addError]
  );

  const handleUnhandledRejection = useCallback(
    (event) => {
      const reason = event.reason;
      const errorMessage =
        reason?.message || String(reason) || "Unhandled promise rejection";

      console.error(
        "[GlobalErrorHandler] Unhandled promise rejection:",
        reason
      );

      addError(`Promise Rejection: ${errorMessage}`, "error");
    },
    [addError]
  );

  useEffect(() => {
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, [handleError, handleUnhandledRejection]);

  // --- Axios response interceptor ---

  useEffect(() => {
    const interceptorId = setupAxiosInterceptor({
      onError: handleInterceptedError,
    });

    return () => {
      teardownAxiosInterceptor(interceptorId);
    };
  }, [handleInterceptedError]);

  // --- Apollo error link callback ref ---

  useEffect(() => {
    errorToastCallback.current = handleInterceptedError;

    return () => {
      errorToastCallback.current = null;
    };
  }, [handleInterceptedError]);

  // Console warnnings and error interception
  // Captures React props types warnings, MUI deprecation warnings, etc.

  useEffect(() => {
    if (!shouldShowConsole) return;

    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      const message = args
        .map((a) => (typeof a === "string" ? a : String(a)))
        .join(" ");
      if (message.includes("Warning:") || message.includes("Deprecated")) {
        addError(message.substring(0, 300), "warning");
      }
    };

    console.error = (...args) => {
      originalError.apply(console, args);
      const message = args
        .map((a) => (typeof a === "string" ? a : String(a)))
        .join(" ");
      if (
        message.includes("[GlobalErrorHandler]") ||
        message.includes("[ApolloErrorLink]")
      ) {
        return;
      }
      if (
        message.includes("Warning:") ||
        message.includes("Error:") ||
        message.includes("Uncaught") ||
        message.includes("Failed")
      ) {
        addError(message.substring(0, 300), "error");
      }
    };

    return () => {
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, [shouldShowConsole, addError]);

  // --- Panel controls ---

  const handleToggle = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const handleClear = useCallback(() => {
    setErrors([]);
    setPanelOpen(false);
  }, []);

  if (!shouldShowConsole) {
    return null;
  }

  return (
    <ErrorConsolePanel
      errors={errors}
      isOpen={panelOpen}
      onToggle={handleToggle}
      onClear={handleClear}
    />
  );
};

export default GlobalErrorHandler;
